import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Phone,
  Mail,
  MessageCircle,
  Send,
  Archive,
  Link2,
  ChevronDown,
  Loader2,
  MessageSquare,
  Hash,
  AlertCircle,
  PhoneMissed,
  PhoneCall,
  MapPin,
  Globe,
  ArrowRightLeft,
  EyeOff,
  Download,
  Reply,
  X,
  File,
  Info,
  ChevronUp,
  MoreHorizontal,
  Pencil,
  Trash2,
  SmilePlus,
  User,
  Landmark,
  Settings,
  Zap,
} from "lucide-react";
import DOMPurify from "dompurify";
import {
  conversationsApi,
  messagesApi,
  timelineApi,
  voiceApi,
  twilioApi,
  emailConnectionsApi,
  usersApi,
  internalCommentsApi,
  PaginatedResponse,
  firmApi,
  automationsApi,
  clientsApi,
} from "../../services/api";
import { formatPhoneNumber } from "../../utils/formatPhone";
import { useTwilioDeviceContext } from "../../contexts/TwilioDeviceContext";
import { useUserDataContext } from "../../context/UserDataContext";
import { useEmailCompose } from "../../contexts/EmailComposeContext";
import NewConversationModal from "./NewConversationModal";
import CallTimelineCard from "./CallTimelineCard";
import CallDetailModal from "./CallDetailModal";
import CallsTab from "./CallsTab";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import Tooltip from "../ui/Tooltip";
import ConversationClientInspectorDrawer from "./ConversationClientInspectorDrawer";
import CommunicationPreferencesModal from "./CommunicationPreferencesModal";

// Province → major city for auto-populating search
const PROVINCE_MAJOR_CITY: Record<string, string> = {
  ON: "Toronto",
  QC: "Montreal",
  BC: "Vancouver",
  AB: "Calgary",
  MB: "Winnipeg",
  SK: "Saskatoon",
  NS: "Halifax",
  NB: "Moncton",
  NL: "St. John's",
  PE: "Charlottetown",
  NT: "Yellowknife",
  NU: "Iqaluit",
  YT: "Whitehorse",
};

const countryOptions = [
  { code: "CA", label: "Canada" },
  { code: "US", label: "United States" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
];

// ── Types ────────────────────────────────────────────────────────────

interface Participant {
  kind: string;
  userId?: string;
  contactId?: string;
  displayName?: string;
  email?: string;
  phoneE164?: string;
  role?: string;
  lastReadAt?: string;
}

interface Conversation {
  _id: string;
  clientId?: string;
  matterId?: string;
  subject?: string;
  preview?: string;
  previewType?: "message" | "call";
  tags: string[];
  participants: Participant[];
  channels: {
    primaryChannel: string;
    inboundPhoneE164?: string;
    fromPhoneE164?: string;
    inboundEmail?: string;
    fromEmail?: string;
  };
  status: string;
  lastMessageAt?: string;
  lastActivityAt?: string;
  counts: {
    totalMessages: number;
    unreadMessages: number;
    totalCalls?: number;
    missedCalls?: number;
  };
  createdAt: string;
}

interface MessageAttachment {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  providerAttachmentId?: string;
}

interface Message {
  _id: string;
  channel: string;
  direction: string;
  author: {
    kind: string;
    displayName?: string;
    email?: string;
    phoneE164?: string;
  };
  recipients: {
    to: string[];
    cc?: string[];
    bcc?: string[];
  };
  subject?: string;
  bodyText?: string;
  bodyHtml?: string;
  attachments?: MessageAttachment[];
  sms?: { segments?: number };
  errors?: { at: string; message: string }[];
  status: string;
  visibility?: "firm" | "private";
  visibilityInfo?: {
    count: number;
    users: { name: string; email: string }[];
  };
  sentAt?: string;
  receivedAt?: string;
  createdAt: string;
}

interface TimelineItem {
  _id: string;
  type: "message" | "call";
  timestamp: string;
  data: any;
  threadId?: string;
  threadMessages?: TimelineItem[];
}

// ── Helpers ──────────────────────────────────────────────────────────

const channelIcon = (channel: string) => {
  switch (channel) {
    case "sms":
      return <Phone className="h-4 w-4" />;
    case "email":
      return <Mail className="h-4 w-4" />;
    case "whatsapp":
      return <MessageCircle className="h-4 w-4" />;
    case "voice":
      return <PhoneCall className="h-4 w-4" />;
    default:
      return <MessageCircle className="h-4 w-4" />;
  }
};

const channelBadgeColor = (channel: string) => {
  switch (channel) {
    case "sms":
      return "bg-blue-100 text-blue-700";
    case "email":
      return "bg-purple-100 text-purple-700";
    case "whatsapp":
      return "bg-green-100 text-green-700";
    case "voice":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-brand-light text-brand";
  }
};

const channelLabel = (channel: string) => {
  switch (channel) {
    case "sms":
      return "SMS";
    case "email":
      return "Email";
    case "whatsapp":
      return "WhatsApp";
    case "voice":
      return "Voice";
    default:
      return "In-App";
  }
};

const formatTime = (dateStr?: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const getConversationName = (c: Conversation) => {
  const primary = c.participants.find((p) => p.role === "primary");
  if (primary?.displayName) return primary.displayName;
  if (primary?.phoneE164) return primary.phoneE164;
  if (primary?.email) return primary.email;
  if (c.channels?.inboundPhoneE164) return c.channels.inboundPhoneE164;
  if (c.subject) return c.subject;
  return "Conversation";
};

const isPhoneNumber = (name: string) => /^\+?\d[\d\s()-]*$/.test(name.trim());

const getInitials = (name: string) => {
  if (isPhoneNumber(name)) return null; // signal to show icon instead
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
};

const smsCharCount = (text: string) => {
  if (text.length <= 160) return { segments: text.length > 0 ? 1 : 0, remaining: 160 - text.length };
  const segments = Math.ceil(text.length / 153);
  const remaining = segments * 153 - text.length;
  return { segments, remaining };
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── HTML Email Helpers ───────────────────────────────────────────────

const processQuotedText = (html: string): string => {
  return html.replace(
    /<div class="gmail_quote">([\s\S]*?)<\/div>/g,
    '<details class="mt-2"><summary class="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Show quoted text</summary><div class="gmail_quote mt-1 border-l-2 border-slate-200 pl-2 text-xs text-slate-500">$1</div></details>'
  );
};

const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(processQuotedText(html), {
    ALLOWED_TAGS: [
      "p", "br", "b", "i", "u", "strong", "em", "a", "ul", "ol", "li",
      "blockquote", "div", "span", "h1", "h2", "h3", "h4", "h5", "h6",
      "img", "table", "tr", "td", "th", "thead", "tbody",
      "details", "summary",
    ],
    ALLOWED_ATTR: ["href", "src", "alt", "class", "style", "target"],
    ALLOW_DATA_ATTR: false,
  });
};

// ── Internal Comments Sub-Component ──────────────────────────────────

interface InternalComment {
  _id: string;
  body: string;
  mentions?: string[];
  authorId: { _id: string; name?: string; email?: string } | string;
  authorName: string | null;
  parentCommentId?: string;
  parentCommentPreview?: string;
  createdAt: string;
  editedAt?: string | null;
  reactions?: Array<{ emoji: string; userId: string; userName?: string }>;
}

interface MessageCommentsProps {
  conversationId: string;
  entityType: "message" | "call_log";
  entityId: string;
  currentUserId?: string;
  currentUserName?: string;
  onCountChange?: (count: number) => void;
}

const QUICK_EMOJIS = ["\uD83D\uDC4D", "\u2764\uFE0F", "\uD83D\uDE02", "\uD83D\uDE2E", "\uD83D\uDE4F", "\u2705"];

const MessageComments: React.FC<MessageCommentsProps> = ({
  conversationId,
  entityType,
  entityId,
  currentUserId,
  currentUserName,
  onCountChange,
}) => {
  const [comments, setComments] = useState<InternalComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Menu & emoji picker state
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [emojiPickerId, setEmojiPickerId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);

  // @mention state
  const [teamMembers, setTeamMembers] = useState<Array<{ _id: string; name: string; email?: string }>>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Close menus on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
        setMenuPos(null);
      }
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setEmojiPickerId(null);
        setPopupPos(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch team members for @mention
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await usersApi.list();
        const users = res.data?.data || res.data || [];
        setTeamMembers(
          users.map((u: any) => ({
            _id: u._id,
            name: u.name || u.email || "Unknown",
            email: u.email,
          }))
        );
      } catch {
        /* ignore */
      }
    };
    fetchTeam();
  }, []);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await internalCommentsApi.list({
        conversationId,
        entityType,
        entityId,
      });
      const fetched = res.data?.data || res.data || [];
      setComments(fetched);
      onCountChange?.(fetched.length);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [conversationId, entityType, entityId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Auto-resize textarea
  const autoResize = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const maxH = 4 * 20;
    ta.style.height = Math.min(ta.scrollHeight, maxH) + "px";
  };

  useEffect(() => {
    autoResize();
  }, [body]);

  const handleSend = async () => {
    if (!body.trim() || sending) return;
    try {
      setSending(true);
      const mentionMatches = body.match(/@[\w\s]+(?=\s|$)/g);
      const mentions = mentionMatches
        ? mentionMatches.map((m) => m.slice(1).trim()).filter(Boolean)
        : [];
      await internalCommentsApi.create({
        conversationId,
        entityType,
        entityId,
        body: body.trim(),
        mentions,
      });
      setBody("");
      fetchComments();
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await internalCommentsApi.remove(commentId);
      setComments((prev) => {
        const updated = prev.filter((c) => c._id !== commentId);
        onCountChange?.(updated.length);
        return updated;
      });
      setConfirmDeleteId(null);
      setMenuOpenId(null);
    } catch {
      /* ignore */
    }
  };

  const handleEdit = async (commentId: string) => {
    if (!editBody.trim() || editSaving) return;
    try {
      setEditSaving(true);
      await internalCommentsApi.update(commentId, { body: editBody.trim() });
      setEditingId(null);
      setEditBody("");
      fetchComments();
    } catch {
      /* ignore */
    } finally {
      setEditSaving(false);
    }
  };

  const handleReaction = async (commentId: string, emoji: string) => {
    try {
      await internalCommentsApi.toggleReaction(commentId, emoji);
      setEmojiPickerId(null);
      fetchComments();
    } catch {
      /* ignore */
    }
  };

  // Highlight @mentions in comment body
  const renderBody = (text: string) => {
    const parts = text.split(/(@[\w\s]+?)(?=\s@|\s*$|[.,!?])/g);
    return parts.map((part, i) =>
      part.startsWith("@") ? (
        <span
          key={i}
          className="bg-blue-100/80 text-blue-700 rounded px-1 py-0.5 text-[11px] font-medium"
        >
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  const formatRelativeTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const getAuthorName = (comment: InternalComment) =>
    comment.authorName ||
    (typeof comment.authorId === "object" ? comment.authorId?.name : null) ||
    "Unknown";

  const getAuthorInitials = (comment: InternalComment) => {
    const name = getAuthorName(comment);
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("");
  };

  const getAuthorId = (comment: InternalComment) =>
    typeof comment.authorId === "object"
      ? comment.authorId?._id
      : comment.authorId;

  const isOwnComment = (comment: InternalComment) =>
    getAuthorId(comment) === currentUserId;

  // Group reactions by emoji
  const groupReactions = (reactions?: InternalComment["reactions"]) => {
    if (!reactions || reactions.length === 0) return [];
    const map = new Map<string, { emoji: string; users: string[]; userIds: string[] }>();
    for (const r of reactions) {
      const existing = map.get(r.emoji);
      if (existing) {
        existing.users.push(r.userName || "Someone");
        existing.userIds.push(r.userId);
      } else {
        map.set(r.emoji, { emoji: r.emoji, users: [r.userName || "Someone"], userIds: [r.userId] });
      }
    }
    return Array.from(map.values());
  };

  // @mention handling
  const filteredMembers = teamMembers.filter((m) =>
    m.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setBody(val);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf("@");

    if (atIndex !== -1) {
      const textBetween = textBeforeCursor.slice(atIndex + 1);
      const charBefore = atIndex > 0 ? val[atIndex - 1] : " ";
      if ((charBefore === " " || charBefore === "\n" || atIndex === 0) && !textBetween.includes("\n")) {
        setMentionStartPos(atIndex);
        setMentionSearch(textBetween);
        setShowMentionDropdown(true);
        setMentionIndex(0);
        return;
      }
    }
    setShowMentionDropdown(false);
    setMentionStartPos(null);
  };

  const insertMention = (memberName: string) => {
    if (mentionStartPos === null) return;
    const before = body.slice(0, mentionStartPos);
    const ta = textareaRef.current;
    const cursorPos = ta?.selectionStart ?? body.length;
    const after = body.slice(cursorPos);
    const newBody = `${before}@${memberName} ${after}`;
    setBody(newBody);
    setShowMentionDropdown(false);
    setMentionStartPos(null);
    setTimeout(() => {
      if (ta) {
        const newPos = before.length + memberName.length + 2;
        ta.focus();
        ta.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionDropdown && filteredMembers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredMembers.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredMembers.length) % filteredMembers.length);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        insertMention(filteredMembers[mentionIndex].name);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Determine visible comments
  const COLLAPSE_THRESHOLD = 5;
  const shouldCollapse = comments.length > COLLAPSE_THRESHOLD && !expanded;
  const hiddenCount = shouldCollapse ? comments.length - 3 : 0;
  const visibleComments = shouldCollapse
    ? comments.slice(comments.length - 3)
    : comments;

  return (
    <div className="mt-1">
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors py-0.5"
      >
        <MessageSquare className="h-3 w-3" />
        <span>
          {comments.length > 0
            ? `Notes \u00B7 ${comments.length}`
            : "Notes"}
        </span>
        {isOpen ? <ChevronUp className="h-3 w-3" /> : comments.length > 0 ? <ChevronDown className="h-3 w-3" /> : null}
      </button>

      {/* Chat thread */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mt-1.5 max-w-[600px] rounded-xl border border-slate-200 bg-slate-50/50 shadow-sm overflow-visible">
              {/* Header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                <EyeOff className="h-3 w-3 text-slate-400" />
                <span className="text-[10px] text-slate-400 font-medium">Internal thread &middot; only your team can see</span>
              </div>

              {/* Messages area */}
              <div className="px-3 py-2 space-y-1 max-h-[320px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center gap-2 py-6">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
                    <span className="text-[11px] text-slate-400">Loading...</span>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-6">
                    <MessageSquare className="h-5 w-5 text-slate-300 mx-auto mb-1.5" />
                    <p className="text-[11px] text-slate-400">No notes yet. Start the conversation.</p>
                  </div>
                ) : (
                  <>
                    {/* Show previous pill */}
                    {shouldCollapse && (
                      <button
                        onClick={() => setExpanded(true)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium hover:bg-slate-200 transition-colors mx-auto mb-2"
                      >
                        <ChevronDown className="h-3 w-3" />
                        {hiddenCount} earlier note{hiddenCount !== 1 ? "s" : ""}
                      </button>
                    )}

                    {expanded && comments.length > COLLAPSE_THRESHOLD && (
                      <button
                        onClick={() => setExpanded(false)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium hover:bg-slate-200 transition-colors mx-auto mb-2"
                      >
                        <ChevronUp className="h-3 w-3" />
                        Show fewer
                      </button>
                    )}

                    {visibleComments.map((comment, idx) => {
                      const own = isOwnComment(comment);
                      const grouped = groupReactions(comment.reactions);
                      const showAvatar =
                        !own &&
                        (idx === 0 || getAuthorId(visibleComments[idx - 1]) !== getAuthorId(comment));
                      const showName = showAvatar;

                      return (
                        <div
                          key={comment._id}
                          className={`flex ${own ? "justify-end" : "justify-start"} group`}
                        >
                          {/* Avatar (left side, other users) */}
                          {!own && (
                            <div className="w-6 flex-shrink-0 mr-1.5 self-end">
                              {showAvatar ? (
                                <div className="h-6 w-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                                  <span className="text-[9px] font-bold text-slate-600 leading-none">
                                    {getAuthorInitials(comment)}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          )}

                          <div className={`max-w-[85%] ${own ? "items-end" : "items-start"} flex flex-col`}>
                            {/* Name (other users, first in group) */}
                            {showName && (
                              <span className="text-[10px] font-medium text-slate-500 mb-0.5 ml-1">
                                {getAuthorName(comment)}
                              </span>
                            )}

                            <div className="relative group/bubble">
                              {/* Edit mode */}
                              {editingId === comment._id ? (
                                <div className={`rounded-2xl px-3 py-2 ${own ? "bg-[#0F5132] rounded-br-md" : "bg-white border border-slate-200 rounded-bl-md"}`}>
                                  <textarea
                                    value={editBody}
                                    onChange={(e) => setEditBody(e.target.value)}
                                    className={`w-full text-xs bg-transparent border-none outline-none resize-none leading-5 min-w-[180px] ${own ? "text-white placeholder-emerald-200" : "text-slate-800 placeholder-slate-400"}`}
                                    rows={2}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleEdit(comment._id);
                                      }
                                      if (e.key === "Escape") {
                                        setEditingId(null);
                                        setEditBody("");
                                      }
                                    }}
                                  />
                                  <div className="flex items-center gap-1.5 justify-end mt-1">
                                    <button
                                      onClick={() => { setEditingId(null); setEditBody(""); }}
                                      className={`text-[10px] px-2 py-0.5 rounded ${own ? "text-emerald-200 hover:text-white" : "text-slate-400 hover:text-slate-600"}`}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleEdit(comment._id)}
                                      disabled={!editBody.trim() || editSaving}
                                      className={`text-[10px] px-2 py-0.5 rounded font-medium ${own ? "text-white bg-white/20 hover:bg-white/30" : "text-[#0F5132] bg-emerald-50 hover:bg-emerald-100"} disabled:opacity-40`}
                                    >
                                      {editSaving ? "Saving..." : "Save"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* Chat bubble */}
                                  <div
                                    className={`rounded-2xl px-3 py-2 ${
                                      own
                                        ? "bg-[#0F5132] text-white rounded-br-md"
                                        : "bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm"
                                    }`}
                                  >
                                    <p className="text-xs whitespace-pre-wrap leading-relaxed">
                                      {renderBody(comment.body)}
                                    </p>
                                  </div>

                                  {/* Hover actions (3-dot + emoji) */}
                                  <div
                                    className={`absolute top-0 ${own ? "left-0 -translate-x-full pr-1" : "right-0 translate-x-full pl-1"} opacity-0 group-hover/bubble:opacity-100 transition-opacity flex items-center gap-0.5`}
                                  >
                                    {/* Add reaction */}
                                    <button
                                      onClick={(e) => {
                                        if (emojiPickerId === comment._id) {
                                          setEmojiPickerId(null);
                                          setPopupPos(null);
                                        } else {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setPopupPos({ top: rect.top - 4, left: rect.left });
                                          setEmojiPickerId(comment._id);
                                        }
                                      }}
                                      className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                      title="React"
                                    >
                                      <SmilePlus className="h-3 w-3" />
                                    </button>

                                    {/* More options (own comments only) */}
                                    {own && (
                                      <div className="relative" ref={menuOpenId === comment._id ? menuRef : undefined}>
                                        <button
                                          onClick={(e) => {
                                            if (menuOpenId === comment._id) {
                                              setMenuOpenId(null);
                                              setMenuPos(null);
                                            } else {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              setMenuPos({ top: rect.bottom + 4, left: rect.right - 120 });
                                              setMenuOpenId(comment._id);
                                            }
                                          }}
                                          className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                          title="Options"
                                        >
                                          <MoreHorizontal className="h-3 w-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  {/* Emoji picker rendered via portal below */}
                                </>
                              )}

                              {/* Reactions row — overlaps bubble bottom edge */}
                              {grouped.length > 0 && editingId !== comment._id && (
                                <div className={`flex flex-wrap gap-1 -mt-1.5 relative z-10 ${own ? "justify-end pr-1" : "justify-start pl-1"}`}>
                                  {grouped.map((g) => {
                                    const hasReacted = currentUserId ? g.userIds.includes(currentUserId) : false;
                                    return (
                                      <button
                                        key={g.emoji}
                                        onClick={() => handleReaction(comment._id, g.emoji)}
                                        title={g.users.join(", ")}
                                        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs shadow-sm transition-colors ${
                                          hasReacted
                                            ? "bg-blue-50 border border-blue-200 text-blue-700"
                                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                                        }`}
                                      >
                                        <span className="text-[11px]">{g.emoji}</span>
                                        {g.users.length > 1 && <span className="text-[10px] font-medium">{g.users.length}</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Timestamp + edited indicator */}
                            <div className={`flex items-center gap-1 mt-0.5 ${own ? "justify-end mr-1" : "justify-start ml-1"}`}>
                              <span className="text-[9px] text-slate-400">
                                {formatRelativeTime(comment.createdAt)}
                              </span>
                              {comment.editedAt && (
                                <span className="text-[9px] text-slate-400 italic">
                                  (edited)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Composer */}
              <div className="relative px-3 py-2 border-t border-slate-100">
                <div className="flex items-end gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
                  <textarea
                    ref={textareaRef}
                    value={body}
                    onChange={handleTextareaChange}
                    onKeyDown={handleTextareaKeyDown}
                    placeholder="Write a note..."
                    rows={1}
                    className="flex-1 text-xs bg-transparent border-none outline-none placeholder-slate-400 resize-none leading-5"
                    style={{ maxHeight: "80px" }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!body.trim() || sending}
                    className="p-1.5 rounded-lg bg-[#0F5132] text-white hover:bg-[#146C43] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0 self-end"
                  >
                    {sending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                  </button>
                </div>

                {/* @mention dropdown */}
                {showMentionDropdown && filteredMembers.length > 0 && (
                  <div className="absolute bottom-full mb-1 left-3 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50 max-h-44 overflow-y-auto">
                    {filteredMembers.map((member, idx) => {
                      const memberInitials = member.name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join("");
                      return (
                        <button
                          key={member._id}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertMention(member.name)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                            idx === mentionIndex
                              ? "bg-slate-100 text-slate-800"
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <div className="h-5 w-5 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center flex-shrink-0">
                            <span className="text-[9px] font-bold text-slate-600 leading-none">
                              {memberInitials || "?"}
                            </span>
                          </div>
                          <span className="truncate font-medium">{member.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Portal: Emoji picker (rendered outside scroll container to avoid clipping) */}
      {emojiPickerId && popupPos && createPortal(
        <div
          ref={emojiRef}
          className="bg-white rounded-xl shadow-lg border border-slate-200 px-2 py-1.5 flex items-center gap-1"
          style={{
            position: "fixed",
            top: popupPos.top,
            left: popupPos.left,
            transform: "translateY(-100%)",
            zIndex: 9999,
          }}
        >
          {QUICK_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => handleReaction(emojiPickerId, emoji)}
              className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-sm transition-colors"
            >
              {emoji}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Portal: Three-dot dropdown menu */}
      {menuOpenId && menuPos && createPortal(
        <div
          ref={menuRef}
          className="bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[120px]"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 9999,
          }}
        >
          <button
            onClick={() => {
              const comment = comments.find((c) => c._id === menuOpenId);
              if (comment) {
                setEditingId(comment._id);
                setEditBody(comment.body);
              }
              setMenuOpenId(null);
              setMenuPos(null);
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
          {confirmDeleteId === menuOpenId ? (
            <button
              onClick={() => handleDelete(menuOpenId)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Confirm delete
            </button>
          ) : (
            <button
              onClick={() => setConfirmDeleteId(menuOpenId)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Message Bubble Component ─────────────────────────────────────────

interface MessageBubbleProps {
  msg: Message;
  isOutbound: boolean;
  selectedConv: Conversation;
  compact?: boolean;
  onReply?: (msg: Message) => void;
  conversationId?: string;
  currentUserId?: string;
  currentUserName?: string;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ msg, isOutbound, selectedConv, compact, onReply, conversationId, currentUserId, currentUserName }) => {
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const visCount = msg.visibilityInfo?.count || 1;
  const visUsers = msg.visibilityInfo?.users || [];
  const visibilityText =
    visCount <= 1
      ? "Only you can see this email"
      : `${visCount} people can see this email`;

  // Detect if email body overflows the max-height clamp
  useEffect(() => {
    if (bodyRef.current) {
      setIsOverflowing(bodyRef.current.scrollHeight > 320);
    }
  }, [msg.bodyHtml, msg.bodyText]);

  return (
    <div className={`flex flex-col ${isOutbound ? "items-end" : "items-start"}`}>
      {/* Message Bubble */}
      <div
        className={`${compact ? "max-w-[65%]" : "max-w-[70%]"} rounded-2xl px-4 py-3 ${
          isOutbound
            ? "text-white rounded-br-md"
            : "bg-white text-slate-800 rounded-bl-md shadow-sm border border-slate-100"
        }`}
        style={isOutbound ? { backgroundColor: "var(--color-primary)" } : {}}
      >
        {/* Channel badge for non-primary channels */}
        {msg.channel && msg.channel !== selectedConv.channels.primaryChannel && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium mb-1 ${
              isOutbound ? "bg-white/20 text-white" : channelBadgeColor(msg.channel)
            }`}
          >
            {channelIcon(msg.channel)}
            {channelLabel(msg.channel)}
          </span>
        )}
        {msg.subject && (
          <p className={`text-xs font-semibold mb-1 ${isOutbound ? "text-emerald-100" : "text-slate-500"}`}>
            <span className="opacity-60">Subject: </span>{msg.subject}
          </p>
        )}
        {/* Email recipients */}
        {msg.channel === "email" && (msg.recipients?.to?.length > 0 || (msg.recipients?.cc && msg.recipients.cc.length > 0)) && (
          <div className={`text-[11px] mb-1.5 space-y-0.5 ${isOutbound ? "text-emerald-200" : "text-slate-400"}`}>
            {msg.recipients?.to?.length > 0 && (
              <p><span className="font-medium">To:</span> {msg.recipients.to.join(", ")}</p>
            )}
            {msg.recipients?.cc && msg.recipients.cc.length > 0 && (
              <p><span className="font-medium">CC:</span> {msg.recipients.cc.join(", ")}</p>
            )}
          </div>
        )}
        <div
          ref={bodyRef}
          className={`relative ${!bodyExpanded && isOverflowing ? "max-h-[320px] overflow-hidden" : ""}`}
        >
          {msg.bodyHtml ? (
            <div
              className={`text-sm prose prose-sm max-w-none ${
                isOutbound
                  ? "[&_a]:!text-emerald-200 [&_blockquote]:!border-emerald-300 [&_blockquote]:!text-emerald-200 !text-white [&_*]:!text-white [&_hr]:!border-white/30"
                  : "[&_a]:text-blue-600 [&_blockquote]:border-slate-300 [&_blockquote]:text-slate-500"
              } [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-xs [&_img]:max-w-full`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.bodyHtml) }}
            />
          ) : (
            <p className="text-sm whitespace-pre-wrap">{msg.bodyText}</p>
          )}
          {!bodyExpanded && isOverflowing && (
            <div className={`absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t ${
              isOutbound ? "from-[var(--color-primary)]" : "from-white"
            } to-transparent`} />
          )}
        </div>
        {isOverflowing && (
          <button
            onClick={() => setBodyExpanded(!bodyExpanded)}
            className={`text-[11px] font-medium mt-1 ${
              isOutbound ? "text-emerald-200 hover:text-white" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            {bodyExpanded ? "Show less" : "Show more"}
          </button>
        )}
        {/* Attachment chips */}
        {msg.attachments && msg.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {msg.attachments.map((att, idx) => (
              <button
                key={idx}
                onClick={async () => {
                  try {
                    const res = await messagesApi.getAttachmentUrl(msg._id, idx);
                    window.open(res.data.url, "_blank");
                  } catch { /* ignore */ }
                }}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                  isOutbound
                    ? "bg-white/15 text-emerald-100 hover:bg-white/25"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                <File className="h-3 w-3 flex-shrink-0" />
                <span className="truncate max-w-[140px]">{att.filename}</span>
                <span className="opacity-60">{formatFileSize(att.sizeBytes)}</span>
                <Download className="h-3 w-3 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
        <div className={`flex items-center gap-2 mt-1.5 ${isOutbound ? "justify-end" : "justify-start"}`}>
          <span className={`text-xs ${isOutbound ? "text-emerald-200" : "text-slate-400"}`}>
            {formatTime(msg.sentAt || msg.receivedAt || msg.createdAt)}
          </span>
          {isOutbound && msg.status && (
            <span className={`text-xs ${msg.status === "failed" ? "text-red-300" : "text-emerald-200"}`}>
              {msg.status === "delivered"
                ? "Delivered"
                : msg.status === "sent"
                  ? "Sent"
                  : msg.status === "failed"
                    ? `Failed${msg.errors?.[0]?.message ? ` — ${msg.errors[0].message}` : ""}`
                    : msg.status === "read"
                      ? "Read"
                      : ""}
            </span>
          )}
        </div>
        {/* Inbound sender info: show name if available, else formatted phone */}
        {!isOutbound && msg.channel === "sms" && (
          <p className="text-[10px] text-slate-400 mt-0.5">
            {msg.author?.displayName && !/^\+?\d[\d\s()-]*$/.test(msg.author.displayName)
              ? msg.author.displayName
              : formatPhoneNumber(msg.author?.phoneE164 || "")}
          </p>
        )}
        {/* Reply in thread button (email only) */}
        {msg.channel === "email" && onReply && (
          <button
            onClick={() => onReply(msg)}
            className={`inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
              isOutbound
                ? "bg-white/10 text-emerald-100 hover:bg-white/20 hover:text-white"
                : "bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700 border border-slate-200"
            }`}
          >
            <Reply className="h-3 w-3" />
            Reply
          </button>
        )}
        {/* Author info for email or outbound messages */}
        {msg.author?.displayName && (isOutbound || msg.channel === "email") && (
          <p className={`text-[10px] mt-0.5 ${isOutbound ? "text-emerald-200 text-right" : "text-slate-400"}`}>
            {msg.author.displayName}
            {msg.channel === "email" && msg.author.email && (
              <span className="ml-1 opacity-70">via {msg.author.email}</span>
            )}
          </p>
        )}
        {msg.visibility === "private" && (
          <Tooltip
            content={
              <div className="space-y-1.5">
                <p className="font-semibold">Private email</p>
                {visCount <= 1 ? (
                  <p>This email is from your personal inbox. Only your account can see it.</p>
                ) : (
                  <div>
                    <p>This email is visible to {visCount} people who have this inbox connected:</p>
                    <ul className="mt-1 space-y-0.5">
                      {visUsers.map((u, i) => (
                        <li key={i} className="text-slate-300">{u.name}{u.email ? ` (${u.email})` : ""}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-slate-400 pt-1 border-t border-slate-600">
                  For team-wide visibility, we recommend creating a shared group email (e.g. info@yourfirm.com) and having each user connect it to Kavoa.
                </p>
              </div>
            }
            position="top"
            maxWidth={320}
          >
            <div className={`flex items-center gap-1 mt-1 cursor-help ${isOutbound ? "justify-end" : "justify-start"}`}>
              <EyeOff className={`h-3 w-3 ${isOutbound ? "text-emerald-200" : "text-slate-400"}`} />
              <span className={`text-[10px] ${isOutbound ? "text-emerald-200" : "text-slate-400"}`}>
                {visibilityText}
              </span>
            </div>
          </Tooltip>
        )}
      </div>

      {/* Action Row (always visible, outside the bubble) */}
      {conversationId && (
        <div className={`${isOutbound ? "pr-1" : "pl-1"}`}>
          <MessageComments
            conversationId={conversationId}
            entityType="message"
            entityId={msg._id}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
          />
        </div>
      )}
    </div>
  );
};

// ── Email Thread Block Component ────────────────────────────────────

interface EmailThreadBlockProps {
  threadMessages: TimelineItem[];
  selectedConv: Conversation;
  onReply?: (msg: Message) => void;
  conversationId?: string;
  currentUserId?: string;
  currentUserName?: string;
}

const EmailThreadBlock: React.FC<EmailThreadBlockProps> = ({ threadMessages, selectedConv, onReply, conversationId, currentUserId, currentUserName }) => {
  const [expanded, setExpanded] = useState(false);
  const latestMsg = threadMessages[threadMessages.length - 1].data as Message;
  const olderMessages = threadMessages.slice(0, -1);
  const latestIsOutbound = latestMsg.direction === "outbound";

  return (
    <div className="space-y-1">
      {/* Collapsed thread indicator */}
      {olderMessages.length > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-2 text-xs text-purple-500 hover:text-purple-700 transition-colors ml-4 mb-1"
        >
          <div className="w-0.5 h-4 bg-purple-300 rounded-full" />
          <span>{olderMessages.length} earlier message{olderMessages.length !== 1 ? "s" : ""} in thread</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      )}

      {/* Expanded older messages */}
      <AnimatePresence>
        {expanded &&
          olderMessages.map((item) => {
            const msg = item.data as Message;
            const isOutbound = msg.direction === "outbound";
            return (
              <motion.div
                key={item._id}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 0.8, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}
              >
                <div className="flex items-stretch">
                  {!isOutbound && <div className="w-0.5 bg-purple-200 mr-2 ml-4 rounded-full" />}
                  <div
                    className={`max-w-[65%] rounded-2xl px-3 py-2 text-xs ${
                      isOutbound
                        ? "text-white rounded-br-md opacity-80"
                        : "bg-slate-50 text-slate-600 rounded-bl-md border border-slate-100"
                    }`}
                    style={isOutbound ? { backgroundColor: "var(--color-primary)" } : {}}
                  >
                    {msg.subject && <p className="font-semibold mb-0.5">{msg.subject}</p>}
                    <p className="whitespace-pre-wrap line-clamp-3">{msg.bodyText}</p>
                    <span className="text-[10px] opacity-60 mt-1 block">
                      {formatTime(msg.sentAt || msg.receivedAt || msg.createdAt)}
                      {msg.author?.displayName && ` - ${msg.author.displayName}`}
                    </span>
                  </div>
                  {isOutbound && <div className="w-0.5 bg-purple-200 ml-2 mr-4 rounded-full" />}
                </div>
              </motion.div>
            );
          })}
      </AnimatePresence>

      {/* Collapse button when expanded */}
      {expanded && olderMessages.length > 0 && (
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center gap-2 text-xs text-purple-500 hover:text-purple-700 transition-colors ml-4 mb-1"
        >
          <div className="w-0.5 h-4 bg-purple-300 rounded-full" />
          <span>Collapse thread</span>
          <ChevronDown className="h-3 w-3 rotate-180" />
        </button>
      )}

      {/* Latest message (full size, always visible) */}
      <MessageBubble msg={latestMsg} isOutbound={latestIsOutbound} selectedConv={selectedConv} onReply={onReply} conversationId={conversationId} currentUserId={currentUserId} currentUserName={currentUserName} />
    </div>
  );
};

// ── Call Timeline Card with Comments Wrapper ────────────────────────

interface CallTimelineCardWithCommentsProps {
  item: TimelineItem;
  conversationId: string;
  currentUserId?: string;
  currentUserName?: string;
  onCallBack?: () => void;
  onTextBack?: () => void;
  onEmailBack?: () => void;
  onViewDetails?: () => void;
}

const CallTimelineCardWithComments: React.FC<CallTimelineCardWithCommentsProps> = ({
  item,
  conversationId,
  currentUserId,
  currentUserName,
  onCallBack,
  onTextBack,
  onEmailBack,
  onViewDetails,
}) => {
  return (
    <div>
      <CallTimelineCard
        callLog={item.data}
        onCallBack={onCallBack}
        onTextBack={onTextBack}
        onEmailBack={onEmailBack}
        onViewDetails={onViewDetails}
      />
      {/* Action row (always visible below card) */}
      <div className="flex justify-center mt-1">
        <MessageComments
          conversationId={conversationId}
          entityType="call_log"
          entityId={item.data?._id || item._id}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
        />
      </div>
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────

interface AvailableNumber {
  phoneNumber: string;
  friendlyName: string;
  city?: string;
  region?: string;
  country: string;
  capabilities: { sms: boolean; voice: boolean; mms: boolean };
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { makeCall, callState } = useTwilioDeviceContext();
  const { userData, firmData, refreshUserData } = useUserDataContext();
  const [portBannerDismissed, setPortBannerDismissed] = useState(() =>
    localStorage.getItem("kavoa_port_banner_dismissed") === "true"
  );

  // Communication preferences banner + modal
  const [commPrefsBannerDismissed, setCommPrefsBannerDismissed] = useState(() =>
    localStorage.getItem("kavoa_comm_prefs_banner_dismissed") === "true"
  );
  const [commPrefsConfigured, setCommPrefsConfigured] = useState<boolean | null>(null);
  const [showCommPrefsModal, setShowCommPrefsModal] = useState(false);

  // Email compose uses the floating popup via useEmailCompose()
  const emailCompose = useEmailCompose();

  // Main tab
  const [mainTab, setMainTab] = useState<"chats" | "calls">("chats");

  // Conversation list state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [hasPhoneNumbers, setHasPhoneNumbers] = useState<boolean | null>(null);
  const [emailConnections, setEmailConnections] = useState<Array<{ _id: string; mailbox: { email: string; displayName?: string }; provider: string }>>([]);
  const [selectedEmailConnectionId, setSelectedEmailConnectionId] = useState<string | null>(null);
  const [convLoading, setConvLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);

  // Timeline state (unified messages + calls)
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [tlLoading, setTlLoading] = useState(false);

  // Compose state (SMS/WhatsApp only  -  email uses floating popup)
  const [composeBody, setComposeBody] = useState("");
  const [composeChannel, setComposeChannel] = useState("sms");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [showChannelDropdown, setShowChannelDropdown] = useState(false);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ totalImported: number; status: string } | null>(null);
  const [showSyncOptions, setShowSyncOptions] = useState(false);

  // Modal state
  const [showNewConvModal, setShowNewConvModal] = useState(false);
  const [newConvPrefill, setNewConvPrefill] = useState<{ client: any; channel: string } | null>(null);

  // Deep link from other pages: /communications?compose=sms&clientId=...
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const compose = searchParams.get("compose");
    const clientId = searchParams.get("clientId");
    if (!compose) return;
    setSearchParams({}, { replace: true });
    if (!clientId) {
      setNewConvPrefill(null);
      setShowNewConvModal(true);
      return;
    }
    clientsApi
      .get(clientId)
      .then((res) => setNewConvPrefill({ client: res.data, channel: compose }))
      .catch(() => setNewConvPrefill(null))
      .finally(() => setShowNewConvModal(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Call detail modal
  const [callDetailOpen, setCallDetailOpen] = useState(false);
  const [selectedCallLog, setSelectedCallLog] = useState<any>(null);

  // Inline Get Number modal
  const [showGetNumber, setShowGetNumber] = useState(false);
  const [gnCountry, setGnCountry] = useState("CA");
  const [gnCity, setGnCity] = useState("");
  const [gnSuggestions, setGnSuggestions] = useState<Array<{ city: string; region: string; areaCodes: number[] }>>([]);
  const [gnShowSuggestions, setGnShowSuggestions] = useState(false);
  const gnSuppressSuggestRef = useRef(false);
  const [gnMatchedCity, setGnMatchedCity] = useState<string | null>(null);
  const [gnIsNearby, setGnIsNearby] = useState(false);
  const [gnSearchDone, setGnSearchDone] = useState(false);
  const [gnNumbers, setGnNumbers] = useState<AvailableNumber[]>([]);
  const [gnLoading, setGnLoading] = useState(false);
  const [gnPurchasing, setGnPurchasing] = useState<string | null>(null);
  const [gnPurchaseError, setGnPurchaseError] = useState("");

  // Inline Connect Email modal
  const [showConnectEmail, setShowConnectEmail] = useState(false);
  const [ceProvider, setCeProvider] = useState("");
  const [ceLoading, setCeLoading] = useState(false);

  // Contact info panel
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Automation status
  const [automationsEnabled, setAutomationsEnabled] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const channelDropdownRef = useRef<HTMLDivElement>(null);

  // ── Fetch Conversations ──────────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    setConvLoading(true);
    try {
      const params: Record<string, any> = { limit: 50 };
      if (search) params.search = search;
      if (channelFilter) params.primaryChannel = channelFilter;
      if (unreadOnly) params.unread = "true";

      const res = await conversationsApi.list(params);
      const data = res.data as PaginatedResponse<Conversation>;
      setConversations(data.data);
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    } finally {
      setConvLoading(false);
    }
  }, [search, channelFilter, unreadOnly]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Check automation config status
  useEffect(() => {
    automationsApi.getConfig()
      .then((res) => {
        if (res.data?.globalEnabled) setAutomationsEnabled(true);
      })
      .catch(() => {});
  }, []);

  // Check if firm has any phone numbers
  useEffect(() => {
    const checkPhoneNumbers = async () => {
      try {
        const res = await twilioApi.listNumbers({ limit: 1 });
        const numbers = res.data?.data || [];
        setHasPhoneNumbers(numbers.length > 0);
      } catch {
        setHasPhoneNumbers(true);
      }
    };
    checkPhoneNumbers();
  }, []);

  // Check if firm has any email connections
  useEffect(() => {
    const checkEmailConnections = async () => {
      try {
        const res = await emailConnectionsApi.list();
        const connections = res.data?.data || res.data || [];
        if (Array.isArray(connections)) {
          setEmailConnections(connections);
          if (connections.length > 0 && !selectedEmailConnectionId) {
            setSelectedEmailConnectionId(connections[0]._id);
          }
        }
      } catch {
        // API may fail (e.g. plan restriction)  -  show banner so user can connect
        setEmailConnections([]);
      }
    };
    checkEmailConnections();
  }, []);

  // Check if firm has communication preferences configured
  useEffect(() => {
    if (commPrefsBannerDismissed) return;
    const checkCommPrefs = async () => {
      try {
        const res = await firmApi.getCommunicationPreferences();
        const prefs = res.data?.communicationPreferences;
        setCommPrefsConfigured(!!prefs);
      } catch {
        setCommPrefsConfigured(false);
      }
    };
    checkCommPrefs();
  }, [commPrefsBannerDismissed]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Fetch Timeline for Selected Conversation ─────────────────────
  const fetchTimeline = useCallback(async (convId: string, quiet = false) => {
    if (!quiet) setTlLoading(true);
    try {
      const res = await timelineApi.list(convId, { limit: 200, groupThreads: "true" });
      const data = res.data as PaginatedResponse<TimelineItem>;
      // API returns newest-first; reverse for chat display (oldest at top)
      setTimelineItems([...(data.data || [])].reverse());
    } catch (err) {
      console.error("Failed to fetch timeline:", err);
    } finally {
      if (!quiet) setTlLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedConv) {
      fetchTimeline(selectedConv._id);
      // Set compose channel based on conversation's primary channel, falling back to an available channel
      const ch = selectedConv.channels.primaryChannel === "voice"
        ? "sms"
        : selectedConv.channels.primaryChannel || "sms";
      const canUseSms = hasPhoneNumbers;
      const canUseEmail = emailConnections.length > 0;
      let resolvedChannel = ch;
      if ((ch === "sms" || ch === "whatsapp") && !canUseSms) {
        resolvedChannel = canUseEmail ? "email" : "sms";
      } else if (ch === "email" && !canUseEmail) {
        resolvedChannel = canUseSms ? "sms" : "email";
      }
      setComposeChannel(resolvedChannel);

      conversationsApi.markRead(selectedConv._id).then(() => {
        // Clear unread badge in local state so UI updates immediately
        setConversations((prev) =>
          prev.map((c) =>
            c._id === selectedConv._id
              ? { ...c, counts: { ...c.counts, unreadMessages: 0, missedCalls: 0 } }
              : c
          )
        );
      }).catch(() => {});
    }
  }, [selectedConv, fetchTimeline, hasPhoneNumbers, emailConnections]);

  // ── Refresh when an email is sent from the floating compose popup ──
  useEffect(() => {
    const unsub = emailCompose.onSent((convId) => {
      fetchTimeline(convId);
      fetchConversations();
    });
    return unsub;
  }, [emailCompose, fetchTimeline, fetchConversations]);

  // ── Auto-sync emails on conversation open + periodic refresh ─────
  // Syncs immediately on render, then every 15s while conversation is open.
  // Stops automatically when navigating away (useEffect cleanup).
  useEffect(() => {
    if (!selectedConv || !selectedEmailConnectionId) return;
    const primary = selectedConv.participants.find((p) => p.role === "primary");
    const contactEmail = primary?.email || selectedConv.channels.inboundEmail;
    if (!contactEmail) return;

    let cancelled = false;

    const backgroundSync = async () => {
      if (cancelled) return;
      try {
        await emailConnectionsApi.sync(selectedEmailConnectionId!, {
          contactEmail,
          conversationId: selectedConv._id,
          after: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          clientId: selectedConv.clientId || undefined,
          matterId: selectedConv.matterId || undefined,
        });
        // Wait for backend to process, then quietly refresh timeline (no loader flash)
        setTimeout(() => {
          if (!cancelled && selectedConv) fetchTimeline(selectedConv._id, true);
        }, 4000);
      } catch {
        // Ignore background sync errors silently
      }
    };

    // Sync immediately on conversation open
    backgroundSync();

    // Re-sync every 15 seconds while this conversation is active
    const interval = setInterval(backgroundSync, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedConv?._id, selectedEmailConnectionId]);

  // ── General timeline polling (SMS/WhatsApp/Voice conversations) ──
  // The email sync above handles email conversations. This covers non-email
  // conversations that still need periodic refresh for incoming messages.
  useEffect(() => {
    if (!selectedConv) return;
    // Skip if the email sync effect is already handling polling
    if (selectedEmailConnectionId) return;

    const interval = setInterval(() => {
      fetchTimeline(selectedConv._id, true);
    }, 8000); // Poll every 8 seconds for SMS/voice conversations

    return () => clearInterval(interval);
  }, [selectedConv?._id, selectedEmailConnectionId]);

  // Scroll to bottom when timeline loads
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [timelineItems]);

  // Refresh timeline when a call ends so the card updates from "Calling..." to final status
  useEffect(() => {
    if (callState.status === "closed" && selectedConv) {
      // Small delay to let the backend endCall update complete
      const timer = setTimeout(() => fetchTimeline(selectedConv._id, true), 1500);
      return () => clearTimeout(timer);
    }
  }, [callState.status]);

  // Close channel dropdown on outside click
  useEffect(() => {
    if (!showChannelDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target as Node)) {
        setShowChannelDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showChannelDropdown]);

  // ── Send Message (SMS/WhatsApp only) ─────────────────────────────
  const handleSend = async () => {
    if (!selectedConv || !composeBody.trim()) return;
    setSending(true);

    try {
      const primary = selectedConv.participants.find((p) => p.role === "primary");
      const to = primary?.phoneE164 || selectedConv.channels.inboundPhoneE164 || "";

      if (!to) {
        setSendError("No recipient address found for this conversation.");
        setSending(false);
        return;
      }

      const from = selectedConv.channels.fromPhoneE164 || "";
      if ((composeChannel === "sms" || composeChannel === "whatsapp") && !from) {
        setSendError("No 'from' phone number configured for this conversation. Please set up a phone number first.");
        setSending(false);
        return;
      }

      const res = await messagesApi.send({
        conversationId: selectedConv._id,
        channel: composeChannel,
        to,
        from: (composeChannel === "sms" || composeChannel === "whatsapp") ? from : undefined,
        body: composeBody,
      });

      // Backend returns 201 even if delivery failed — check sendResult
      const sr = res.data?.sendResult;
      if (sr && !sr.success && sr.error) {
        setSendError(`Message delivery failed: ${sr.error}`);
      }

      setComposeBody("");
      fetchTimeline(selectedConv._id);
      fetchConversations();
    } catch (err: any) {
      console.error("Failed to send message:", err);
      const data = err?.response?.data;
      if (Array.isArray(data?.errors)) {
        setSendError(data.errors.map((e: any) => e.message).join("; "));
      } else if (data?.message) {
        setSendError(data.message);
      } else if (data?.error) {
        setSendError(data.error);
      } else {
        setSendError(`Failed to send message (${err?.response?.status || "network error"})`);
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Reply in Thread (opens floating email compose popup) ──────────
  const handleReply = (msg: Message) => {
    if (!selectedConv) return;
    const primary = selectedConv.participants.find((p) => p.role === "primary");
    const replyTo = msg.author?.email || primary?.email || selectedConv.channels.inboundEmail || "";
    emailCompose.openReply({
      conversationId: selectedConv._id,
      replyToMessageId: msg._id,
      subject: msg.subject || "",
      to: replyTo ? [replyTo] : [],
      emailConnectionId: selectedEmailConnectionId || undefined,
      clientId: selectedConv.clientId || undefined,
    });
  };

  // ── Compose Email (opens floating popup for new email) ────────────
  const handleComposeEmail = () => {
    if (!selectedConv) return;
    const primary = selectedConv.participants.find((p) => p.role === "primary");
    const primaryEmail = primary?.email || selectedConv.channels.inboundEmail || "";
    emailCompose.openCompose({
      to: primaryEmail ? [primaryEmail] : [],
      conversationId: selectedConv._id,
      emailConnectionId: selectedEmailConnectionId || undefined,
      clientId: selectedConv.clientId || undefined,
    });
  };

  // ── Sync Emails ────────────────────────────────────────────────────
  const handleSyncEmails = async (after?: string) => {
    if (!selectedConv || !selectedEmailConnectionId) return;
    const primary = selectedConv.participants.find((p) => p.role === "primary");
    const contactEmail = primary?.email || selectedConv.channels.inboundEmail;
    if (!contactEmail) return;

    setSyncing(true);
    setSyncProgress({ totalImported: 0, status: "in_progress" });
    setShowSyncOptions(false);

    try {
      await emailConnectionsApi.sync(selectedEmailConnectionId, {
        contactEmail,
        conversationId: selectedConv._id,
        after: after || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        clientId: selectedConv.clientId || undefined,
        matterId: selectedConv.matterId || undefined,
      });

      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          const res = await emailConnectionsApi.syncStatus(selectedEmailConnectionId!, contactEmail);
          setSyncProgress(res.data);
          if (res.data.status === "completed" || res.data.status === "idle") {
            clearInterval(pollInterval);
            setSyncing(false);
            if (selectedConv) fetchTimeline(selectedConv._id);
          }
        } catch {
          clearInterval(pollInterval);
          setSyncing(false);
        }
      }, 2000);
    } catch (err) {
      console.error("Sync failed:", err);
      setSyncing(false);
    }
  };

  // ── Archive Conversation ─────────────────────────────────────────
  const handleArchive = async () => {
    if (!selectedConv) return;
    try {
      await conversationsApi.archive(selectedConv._id);
      setSelectedConv(null);
      fetchConversations();
    } catch (err) {
      console.error("Failed to archive:", err);
    }
  };

  // ── Call Actions ─────────────────────────────────────────────────
  const handleMakeCall = () => {
    if (!selectedConv) return;
    const primary = selectedConv.participants.find((p) => p.role === "primary");
    const to = primary?.phoneE164 || selectedConv.channels.inboundPhoneE164;
    const from = selectedConv.channels.fromPhoneE164;
    if (to && from) {
      makeCall(to, from, selectedConv._id);
    }
  };

  const handleCallBack = (callLog: any) => {
    if (!selectedConv) return;
    const to = callLog.from?.phoneE164 || callLog.to?.phoneE164;
    const from = selectedConv.channels.fromPhoneE164;
    if (to && from) {
      makeCall(to, from, selectedConv._id);
    }
  };

  const handleTextBack = (callLog: any) => {
    const phone = callLog.from?.phoneE164 || callLog.to?.phoneE164;
    if (phone) {
      setComposeChannel("sms");
      setComposeBody("");
    }
  };

  const handleEmailBack = () => {
    setComposeChannel("email");
    setComposeBody("");
  };

  const handleUpdateCallNotes = async (notes: string) => {
    if (!selectedCallLog) return;
    try {
      await voiceApi.updateCallNotes(selectedCallLog._id, notes);
    } catch (err) {
      console.error("Failed to update call notes:", err);
    }
  };

  // ── Inline Get Number: City Autocomplete ─────────────────────────
  useEffect(() => {
    if (!gnCity || gnCity.length < 2) {
      setGnSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      if (gnSuppressSuggestRef.current) { gnSuppressSuggestRef.current = false; return; }
      try {
        const res = await twilioApi.suggestCities({ q: gnCity, country: gnCountry });
        setGnSuggestions(res.data.data || []);
        setGnShowSuggestions(true);
      } catch {
        setGnSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [gnCity, gnCountry]);

  const handleGnSearch = async () => {
    setGnLoading(true);
    setGnSearchDone(false);
    try {
      const res = await twilioApi.searchAvailable({ country: gnCountry, city: gnCity || undefined });
      setGnNumbers(res.data.data || []);
      setGnMatchedCity(res.data.matchedCity || null);
      setGnIsNearby(res.data.isNearby || false);
      setGnSearchDone(true);
    } catch {
      setGnSearchDone(true);
    } finally {
      setGnLoading(false);
    }
  };

  // Auto-search when modal opens based on firm province
  useEffect(() => {
    if (!showGetNumber) return;
    const province = firmData?.jurisdiction?.region?.toUpperCase();
    const city = province ? PROVINCE_MAJOR_CITY[province] : null;
    if (city && !gnSearchDone && gnNumbers.length === 0) {
      setGnCity(city);
      (async () => {
        setGnLoading(true);
        setGnSearchDone(false);
        try {
          const res = await twilioApi.searchAvailable({ country: "CA", city });
          setGnNumbers(res.data.data || []);
          setGnMatchedCity(res.data.matchedCity || null);
          setGnIsNearby(res.data.isNearby || false);
          setGnSearchDone(true);
        } catch {
          setGnSearchDone(true);
        } finally {
          setGnLoading(false);
        }
      })();
    }
  }, [showGetNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGnPurchase = async (phoneNumber: string) => {
    setGnPurchasing(phoneNumber);
    setGnPurchaseError("");
    try {
      await twilioApi.purchase({ phoneNumber, country: gnCountry, purpose: "general" });
      setShowGetNumber(false);
      setHasPhoneNumbers(true);
      setGnNumbers([]);
      setGnCity("");
      setGnSearchDone(false);
    } catch (err: any) {
      console.error("Failed to purchase:", err);
      const data = err?.response?.data;
      const status = err?.response?.status;

      if (status === 403 && data?.error === "upgrade_required") {
        setGnPurchaseError(
          `This feature requires the ${data.requiredPlan || "basic"} plan or higher. Please upgrade your subscription to purchase phone numbers.`
        );
      } else if (status === 402 && data?.error === "twilio_trial") {
        setGnPurchaseError(data.message || "Your Twilio account is on a trial plan. Upgrade at twilio.com to purchase numbers.");
      } else if (status === 402 && data?.error === "insufficient_funds") {
        setGnPurchaseError(data.message || "Insufficient funds in your Twilio account.");
      } else if (status === 409 && data?.error === "number_unavailable") {
        setGnPurchaseError(data.message || "This number is no longer available. Try a different one.");
      } else if (status === 503) {
        setGnPurchaseError(data?.message || "Phone service is not configured. Check your Twilio credentials in the server environment.");
      } else if (data?.message) {
        setGnPurchaseError(data.message);
      } else if (data?.details) {
        setGnPurchaseError(data.details);
      } else if (data?.error && data.error !== "purchase_failed") {
        setGnPurchaseError(data.error);
      } else {
        setGnPurchaseError("Failed to purchase this number. Please try again or contact support.");
      }
    } finally {
      setGnPurchasing(null);
    }
  };

  // ── Inline Connect Email (OAuth) ──
  const handleEmailOAuth = async (provider: "gmail" | "microsoft") => {
    setCeLoading(true);
    try {
      const res = await emailConnectionsApi.getAuthUrl(provider);
      const { authUrl } = res.data;
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err: any) {
      console.error(`Failed to get ${provider} auth URL:`, err);
      setCeLoading(false);
    }
  };

  // ── Filter Tabs ──────────────────────────────────────────────────
  const filterTabs = [
    { key: "", label: "All" },
    { key: "unread", label: "Unread" },
    { key: "sms", label: "SMS" },
    { key: "email", label: "Email" },
    { key: "whatsapp", label: "WhatsApp" },
  ];

  const activeFilter = unreadOnly ? "unread" : channelFilter;

  const handleFilterClick = (key: string) => {
    if (key === "unread") {
      setUnreadOnly(true);
      setChannelFilter("");
    } else {
      setUnreadOnly(false);
      setChannelFilter(key);
    }
  };

  const smsInfo = composeChannel === "sms" || composeChannel === "whatsapp" ? smsCharCount(composeBody) : null;
  const hasEmailConnections = emailConnections.length > 0;

  // ── Compose channel options ──────────────────────────────────────
  // Always show all channels; disabled ones show a prompt instead of switching
  const composeChannelOptions: { key: string; label: string; icon: React.FC<any>; disabled?: boolean; disabledMsg?: string }[] = [
    { key: "sms", label: "SMS", icon: Phone, disabled: !hasPhoneNumbers, disabledMsg: "Add a phone number to send texts" },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, disabled: !hasPhoneNumbers, disabledMsg: "Add a phone number for WhatsApp" },
    ...(hasEmailConnections ? [{ key: "email", label: "Email", icon: Mail }] : []),
  ];

  // Channel switching  -  email opens the floating compose popup
  const handleChannelSwitch = (newChannel: string) => {
    if (newChannel === "email") {
      handleComposeEmail();
      setShowChannelDropdown(false);
      return;
    }
    setComposeChannel(newChannel);
    setShowChannelDropdown(false);
  };

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Communications</h1>
          <p className="text-sm text-slate-500 mt-1">
            {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => navigate("/communications/automations")}
            className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors shadow-sm"
            style={{ backgroundColor: "#0F5132" }}
          >
            {automationsEnabled && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 border-2 border-white" />
            )}
            <Zap className="h-4 w-4" />
            Automations
          </button>
        </div>
        {firmData.primaryPhone && hasPhoneNumbers === false && (
          <div className="flex items-center gap-2.5 px-4 py-2.5 bg-white rounded-xl border border-slate-200 shadow-sm">
            <Phone className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">{firmData.primaryPhone}</span>
            <span className="text-slate-300">|</span>
            <button
              onClick={() => navigate("/settings/port-guide")}
              className="text-sm font-semibold hover:underline transition-colors"
              style={{ color: "var(--color-accent)" }}
            >
              Port to Kavoa
            </button>
          </div>
        )}
      </div>

      {/* Port Number Recommendation Banner */}
      {hasPhoneNumbers === false && !portBannerDismissed && (
        <div className="mb-4 bg-cyan-50 border border-cyan-200 rounded-xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
              <Landmark className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {firmData.primaryPhone
                  ? `Your firm number (${firmData.primaryPhone}) can't be used in Kavoa yet`
                  : "Set up a phone number to start messaging"}
              </p>
              <p className="text-xs text-slate-500">
                {firmData.primaryPhone
                  ? "Port it to Kavoa to enable texting, even landlines gain SMS capability. Or get a new dedicated number for use in Kavoa."
                  : "Port your existing number or get a new one to send SMS and make calls from Kavoa."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => navigate("/settings/port-guide")}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-white rounded-xl text-sm font-bold transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              <ArrowRightLeft className="h-3.5 w-3.5" />
              Port My Number
            </button>
            <button
              onClick={() => setShowGetNumber(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Get a New Number
            </button>
            <button
              onClick={() => {
                setPortBannerDismissed(true);
                localStorage.setItem("kavoa_port_banner_dismissed", "true");
              }}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Email Connection Banner */}
      {hasEmailConnections === false && (
        <div className="mb-4 bg-purple-50 border border-purple-200 rounded-xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Connect your email</p>
              <p className="text-xs text-slate-500">
                Link your Gmail, Outlook, or other email to send and receive emails alongside SMS and calls
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowConnectEmail(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 transition-colors flex-shrink-0"
          >
            <Mail className="h-4 w-4" />
            Connect Email
          </button>
        </div>
      )}

      {/* Communication Preferences Banner */}
      <AnimatePresence>
        {!commPrefsBannerDismissed && commPrefsConfigured === false && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-xl px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Settings className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Set up your communication preferences
                  </p>
                  <p className="text-xs text-slate-500">
                    Configure how your clients receive notifications about invoices, appointments, and more.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowCommPrefsModal(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-white rounded-xl text-sm font-bold transition-colors"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
                >
                  Configure Now
                </button>
                <button
                  onClick={() => {
                    setCommPrefsBannerDismissed(true);
                    localStorage.setItem("kavoa_comm_prefs_banner_dismissed", "true");
                  }}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Tabs: Chats / Calls + New Conversation */}
      <div className="flex items-center gap-1 mb-4">
        {[
          { key: "chats" as const, label: "Chats", icon: MessageSquare },
          { key: "calls" as const, label: "Calls", icon: PhoneCall },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = mainTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setMainTab(tab.key)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive ? "text-white" : "text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
              }`}
              style={isActive ? { backgroundColor: "var(--color-primary)" } : {}}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
        <div className="flex-1" />
        <button
          onClick={() => {
            setNewConvPrefill(null);
            setShowNewConvModal(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="h-4 w-4" />
          New Conversation
        </button>
      </div>

      {/* Calls Tab */}
      {mainTab === "calls" && (
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-0">
          <CallsTab
            onViewDetails={(callLog) => {
              setSelectedCallLog(callLog);
              setCallDetailOpen(true);
            }}
          />
        </div>
      )}

      {/* Chats Tab */}
      {mainTab === "chats" && (
        <div className="flex-1 flex bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" style={{ minHeight: "700px" }}>
          {/* ── Left Panel: Conversation List ── */}
          <div className="w-1/3 border-r border-slate-200 flex flex-col min-h-0">
            {/* Search */}
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 px-3 py-2 border-b border-slate-100 flex-wrap">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleFilterClick(tab.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    activeFilter === tab.key ? "text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                  style={activeFilter === tab.key ? { backgroundColor: "var(--color-primary)" } : {}}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {convLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                </div>
              ) : conversations.length === 0 ? (
                channelFilter === "email" && hasEmailConnections === false ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="h-20 w-20 rounded-full bg-purple-100 flex items-center justify-center mb-5">
                      <Mail className="h-10 w-10 text-purple-700" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Connect Your Email</h3>
                    <p className="text-sm text-slate-500 max-w-xs mb-6">
                      Link your Gmail, Outlook, or other email to send and receive emails alongside SMS and calls.
                    </p>
                    <button
                      onClick={() => setShowConnectEmail(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold transition-colors bg-purple-600 hover:bg-purple-700"
                    >
                      <Mail className="h-4 w-4" />
                      Connect Email
                    </button>
                  </div>
                ) : channelFilter === "whatsapp" ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mb-5">
                      <MessageCircle className="h-10 w-10 text-green-700" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Connect WhatsApp</h3>
                    <p className="text-sm text-slate-500 max-w-xs mb-6">
                      Link your WhatsApp Business account to message clients on the world's most popular messaging app.
                    </p>
                    <button
                      onClick={() => setShowGetNumber(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold transition-colors bg-green-600 hover:bg-green-700"
                    >
                      <Phone className="h-4 w-4" />
                      Get a Phone Number
                    </button>
                  </div>
                ) : (channelFilter === "" || channelFilter === "sms") && hasPhoneNumbers === false ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="h-20 w-20 rounded-full bg-brand-light flex items-center justify-center mb-5">
                      <Phone className="h-10 w-10 text-brand" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Get Started with Communications</h3>
                    <p className="text-sm text-slate-500 max-w-xs mb-6">
                      Set up a phone number to send and receive SMS messages and make calls to your clients.
                    </p>
                    <button
                      onClick={() => setShowGetNumber(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold transition-colors"
                      style={{ backgroundColor: "var(--color-primary)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                    >
                      <Phone className="h-4 w-4" />
                      Get a Phone Number
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500 px-4">
                    <MessageSquare className="h-10 w-10 mb-3 text-slate-300" />
                    <p className="font-medium text-sm">No conversations yet</p>
                    <p className="text-xs mt-1 text-center">Start a new conversation to begin messaging</p>
                  </div>
                )
              ) : (
                conversations.map((conv) => {
                  const name = getConversationName(conv);
                  const isSelected = selectedConv?._id === conv._id;
                  const lastTime = conv.lastActivityAt || conv.lastMessageAt;
                  return (
                    <motion.div
                      key={conv._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => setSelectedConv(conv)}
                      className={`px-4 py-3 border-b border-slate-50 cursor-pointer transition-colors ${
                        isSelected ? "bg-brand-light border-l-2 border-l-brand-accent" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div
                          className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                          style={{ backgroundColor: getInitials(name) ? "var(--color-accent)" : "#94a3b8" }}
                        >
                          {getInitials(name) || <User className="h-5 w-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={`text-sm truncate ${
                                conv.counts.unreadMessages > 0 || (conv.counts.missedCalls || 0) > 0
                                  ? "font-bold text-slate-900"
                                  : "font-medium text-slate-800"
                              }`}
                            >
                              {name}
                            </span>
                            <span className="text-xs text-slate-400 flex-shrink-0">{formatTime(lastTime)}</span>
                          </div>
                          {conv.subject && conv.channels?.primaryChannel === "email" && (
                            <p className="text-xs text-slate-400 truncate">{conv.subject}</p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5">
                            {channelFilter && (
                              <span
                                className={`inline-flex items-center gap-1 text-xs ${channelBadgeColor(
                                  conv.channels.primaryChannel
                                )}`}
                                style={{ padding: "2px", borderRadius: "4px" }}
                              >
                                {channelIcon(conv.channels.primaryChannel)}
                              </span>
                            )}
                            {/* Missed call icon in preview */}
                            {conv.previewType === "call" && conv.preview?.toLowerCase().includes("missed") && (
                              <PhoneMissed className="h-3 w-3 text-red-500 flex-shrink-0" />
                            )}
                            <p
                              className={`text-xs truncate flex-1 ${
                                conv.counts.unreadMessages > 0 || (conv.counts.missedCalls || 0) > 0
                                  ? "text-slate-700 font-medium"
                                  : "text-slate-500"
                              }`}
                            >
                              {conv.preview || "No messages yet"}
                            </p>
                            {/* Unread badge */}
                            {conv.counts.unreadMessages > 0 && (
                              <span
                                className="flex-shrink-0 h-5 min-w-[20px] px-1.5 rounded-full text-xs font-bold text-white flex items-center justify-center"
                                style={{ backgroundColor: "var(--color-accent)" }}
                              >
                                {conv.counts.unreadMessages}
                              </span>
                            )}
                            {/* Missed calls badge */}
                            {(conv.counts.missedCalls || 0) > 0 && (
                              <span className="flex-shrink-0 h-5 min-w-[20px] px-1.5 rounded-full text-xs font-bold text-white bg-red-500 flex items-center justify-center">
                                {conv.counts.missedCalls}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Right Panel: Thread View ── */}
          <div className="flex-1 flex min-h-0">
           <div className="flex-1 flex flex-col min-h-0">
            {!selectedConv ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                <MessageSquare className="h-16 w-16 mb-4 text-slate-200" />
                <p className="text-lg font-medium text-slate-500">Select a conversation</p>
                <p className="text-sm mt-1">Choose a conversation from the left panel to view messages</p>
              </div>
            ) : (
              <>
                {/* Thread Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      onClick={() => setShowInfoPanel(true)}
                      className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: getInitials(getConversationName(selectedConv)) ? "var(--color-accent)" : "#94a3b8" }}
                    >
                      {getInitials(getConversationName(selectedConv)) || <User className="h-5 w-5" />}
                    </div>
                    <div>
                      <h2
                        onClick={() => setShowInfoPanel(true)}
                        className="text-sm font-bold text-slate-800 cursor-pointer hover:text-emerald-700 transition-colors"
                      >
                        {getConversationName(selectedConv)}
                      </h2>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${channelBadgeColor(
                            selectedConv.channels.primaryChannel
                          )}`}
                        >
                          {channelIcon(selectedConv.channels.primaryChannel)}
                          {channelLabel(selectedConv.channels.primaryChannel)}
                        </span>
                        {selectedConv.matterId && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            <Link2 className="h-3 w-3" />
                            Linked
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Pull emails button */}
                    {emailConnections.length > 0 && selectedConv.channels.primaryChannel === "email" && (
                      <div className="relative">
                        <button
                          onClick={() => syncing ? null : handleSyncEmails()}
                          onContextMenu={(e) => { e.preventDefault(); setShowSyncOptions(!showSyncOptions); }}
                          disabled={syncing}
                          className="p-2 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors disabled:opacity-50"
                          title="Pull emails from Gmail"
                        >
                          {syncing ? (
                            <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                          ) : (
                            <Download className="h-5 w-5" />
                          )}
                        </button>
                        {syncing && syncProgress && syncProgress.totalImported > 0 && (
                          <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center justify-center">
                            {syncProgress.totalImported}
                          </span>
                        )}
                        {showSyncOptions && !syncing && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50 w-44">
                            <button
                              onClick={() => handleSyncEmails()}
                              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Last 1 month
                            </button>
                            <button
                              onClick={() => handleSyncEmails(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString())}
                              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Last 3 months
                            </button>
                            <button
                              onClick={() => handleSyncEmails(new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString())}
                              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Last 6 months
                            </button>
                            <button
                              onClick={() => handleSyncEmails(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString())}
                              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              Last 1 year
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Call button */}
                    <button
                      onClick={handleMakeCall}
                      className="p-2 rounded-lg text-slate-400 hover:text-brand-accent hover:bg-brand-light transition-colors"
                      title="Start a call"
                    >
                      <Phone className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleArchive}
                      className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                      title="Archive"
                    >
                      <Archive className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setShowInfoPanel(!showInfoPanel)}
                      className={`p-2 rounded-lg transition-colors ${
                        showInfoPanel
                          ? "text-emerald-600 bg-emerald-50"
                          : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      }`}
                      title="Contact info"
                    >
                      <Info className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Timeline Area (Messages + Call Cards) */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" style={{ backgroundColor: "#F4F6F8" }}>
                  {tlLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                    </div>
                  ) : timelineItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                      <MessageCircle className="h-10 w-10 mb-2 text-slate-300" />
                      <p className="text-sm">No messages yet. Send the first one!</p>
                    </div>
                  ) : (
                    timelineItems.map((item) => {
                      if (item.type === "call") {
                        return (
                          <CallTimelineCardWithComments
                            key={item._id}
                            item={item}
                            conversationId={selectedConv._id}
                            currentUserId={userData?._id}
                            currentUserName={userData?.name}
                            onCallBack={() => handleCallBack(item.data)}
                            onTextBack={() => handleTextBack(item.data)}
                            onEmailBack={handleEmailBack}
                            onViewDetails={() => {
                              setSelectedCallLog(item.data);
                              setCallDetailOpen(true);
                            }}
                          />
                        );
                      }

                      // Threaded email group
                      if (item.threadMessages && item.threadMessages.length > 1) {
                        return (
                          <EmailThreadBlock
                            key={item._id}
                            threadMessages={item.threadMessages}
                            selectedConv={selectedConv}
                            onReply={handleReply}
                            conversationId={selectedConv._id}
                            currentUserId={userData?._id}
                            currentUserName={userData?.name}
                          />
                        );
                      }

                      // Single message bubble
                      const msg = item.data as Message;
                      const isOutbound = msg.direction === "outbound";
                      return (
                        <MessageBubble key={item._id} msg={msg} isOutbound={isOutbound} selectedConv={selectedConv} onReply={handleReply} conversationId={selectedConv._id} currentUserId={userData?._id} currentUserName={userData?.name} />
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Send error banner */}
                {sendError && (
                  <div className="mx-6 mt-3 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 flex-1">{sendError}</span>
                    <button
                      onClick={() => setSendError("")}
                      className="p-0.5 rounded text-red-400 hover:text-red-600 flex-shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* Compose Area */}
                <div className="border-t border-slate-200 px-6 py-4 bg-white">
                  {composeChannel === "email" ? (
                    /* Email mode: "New Email" button + channel switcher to go back to SMS */
                    <div className="flex items-center gap-3">
                      <div className="relative" ref={channelDropdownRef}>
                        <button
                          onClick={() => setShowChannelDropdown(!showChannelDropdown)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-medium transition-colors hover:bg-slate-50 ${channelBadgeColor(
                            composeChannel
                          )}`}
                        >
                          {channelIcon(composeChannel)}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <AnimatePresence>
                          {showChannelDropdown && (
                            <motion.div
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 4 }}
                              className="absolute bottom-12 left-0 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-10 w-56"
                            >
                              {composeChannelOptions.map((ch) => (
                                <button
                                  key={ch.key}
                                  onClick={() => {
                                    if (ch.disabled) {
                                      setShowChannelDropdown(false);
                                      setShowGetNumber(true);
                                      return;
                                    }
                                    handleChannelSwitch(ch.key);
                                  }}
                                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
                                    ch.disabled
                                      ? "text-slate-400"
                                      : "text-slate-700 hover:bg-slate-50"
                                  }`}
                                >
                                  <ch.icon className="h-4 w-4" />
                                  <span className="flex-1 text-left">
                                    {ch.label}
                                    {ch.disabled && (
                                      <span className="block text-[10px] text-slate-400">{ch.disabledMsg}</span>
                                    )}
                                  </span>
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <button
                        onClick={handleComposeEmail}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-purple-200 bg-purple-50 text-purple-700 text-sm font-medium hover:bg-purple-100 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        New Email
                      </button>
                    </div>
                  ) : (
                    /* SMS/WhatsApp: inline compose */
                    <div className="flex items-end gap-3">
                      {/* Channel Switcher */}
                      <div className="relative" ref={channelDropdownRef}>
                        <button
                          onClick={() => setShowChannelDropdown(!showChannelDropdown)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-medium transition-colors hover:bg-slate-50 ${channelBadgeColor(
                            composeChannel
                          )}`}
                        >
                          {channelIcon(composeChannel)}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <AnimatePresence>
                          {showChannelDropdown && (
                            <motion.div
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 4 }}
                              className="absolute bottom-12 left-0 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-10 w-56"
                            >
                              {composeChannelOptions.map((ch) => (
                                <button
                                  key={ch.key}
                                  onClick={() => {
                                    if (ch.disabled) {
                                      setShowChannelDropdown(false);
                                      setShowGetNumber(true);
                                      return;
                                    }
                                    handleChannelSwitch(ch.key);
                                  }}
                                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
                                    ch.disabled
                                      ? "text-slate-400"
                                      : "text-slate-700 hover:bg-slate-50"
                                  }`}
                                >
                                  <ch.icon className="h-4 w-4" />
                                  <span className="flex-1 text-left">
                                    {ch.label}
                                    {ch.disabled && (
                                      <span className="block text-[10px] text-slate-400">{ch.disabledMsg}</span>
                                    )}
                                  </span>
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Message input */}
                      <div className="flex-1">
                        <textarea
                          value={composeBody}
                          onChange={(e) => setComposeBody(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Type a message..."
                          rows={1}
                          className="compose-textarea w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 resize-none"
                        />

                        {/* SMS character count */}
                        {(composeChannel === "sms" || composeChannel === "whatsapp") && composeBody.length > 0 && smsInfo && (
                          <div className="flex items-center gap-3 mt-1 px-1">
                            <span className="text-xs text-slate-400">
                              {composeBody.length} character{composeBody.length !== 1 ? "s" : ""}
                            </span>
                            <span className="text-xs text-slate-400">
                              <Hash className="inline h-3 w-3 mr-0.5" />
                              {smsInfo.segments} segment{smsInfo.segments !== 1 ? "s" : ""}
                            </span>
                            {composeBody.length > 1600 && (
                              <span className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Long message
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Send Button */}
                      <button
                        onClick={handleSend}
                        disabled={sending || !composeBody.trim()}
                        className="p-3 rounded-xl text-white transition-colors disabled:opacity-40"
                        style={{ backgroundColor: "var(--color-primary)" }}
                        onMouseEnter={(e) => {
                          if (!sending && composeBody.trim()) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
                        }}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                      >
                        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
           </div>
           <AnimatePresence>
             {showInfoPanel && selectedConv && (
               <ConversationClientInspectorDrawer
                 conversation={selectedConv}
                 open={showInfoPanel}
                 onClose={() => setShowInfoPanel(false)}
               />
             )}
           </AnimatePresence>
          </div>
        </div>
      )}

      {/* Modals */}
      <NewConversationModal
        open={showNewConvModal}
        initialRecipient={newConvPrefill?.client}
        initialChannel={newConvPrefill?.channel}
        onClose={() => {
          setShowNewConvModal(false);
          setNewConvPrefill(null);
        }}
        onCreated={(conv) => {
          setShowNewConvModal(false);
          setNewConvPrefill(null);
          fetchConversations();
          setSelectedConv(conv);
          // Auto-dial if voice channel
          if (conv.channels?.primaryChannel === "voice") {
            const primary = conv.participants?.find((p: any) => p.role === "primary");
            const to = primary?.phoneE164 || conv.channels.inboundPhoneE164;
            const from = conv.channels.fromPhoneE164;
            if (to && from) {
              setTimeout(() => makeCall(to, from, conv._id), 300);
            }
          }
        }}
      />

      <CallDetailModal
        open={callDetailOpen}
        onClose={() => {
          setCallDetailOpen(false);
          setSelectedCallLog(null);
        }}
        callLog={selectedCallLog}
        onUpdateNotes={handleUpdateCallNotes}
        currentUserId={userData?._id}
        currentUserName={userData?.name}
      />

      {/* ── Get a Number Modal (Inline) ── */}
      <PremiumModal
        open={showGetNumber}
        onClose={() => {
          setShowGetNumber(false);
          setGnNumbers([]);
          setGnCity("");
          setGnSearchDone(false);
          setGnMatchedCity(null);
          setGnPurchaseError("");
        }}
        title="Get a Number"
        subtitle="Search and purchase a new phone number"
        icon={Phone}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <SearchableSelect
                options={countryOptions.map((c) => ({
                  value: c.code,
                  label: c.label,
                  icon: Globe,
                }))}
                value={gnCountry}
                onChange={(val) => {
                  setGnCountry(val || "CA");
                  setGnCity("");
                  setGnSuggestions([]);
                  setGnMatchedCity(null);
                }}
                placeholder="Select country"
                icon={Globe}
              />
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="e.g. Toronto, Vancouver"
                  value={gnCity}
                  onChange={(e) => {
                    setGnCity(e.target.value);
                    setGnMatchedCity(null);
                  }}
                  onFocus={() => gnSuggestions.length > 0 && setGnShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setGnShowSuggestions(false), 150)}
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
              {gnShowSuggestions && gnSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-lg max-h-48 overflow-y-auto">
                  {gnSuggestions.map((s) => (
                    <button
                      key={`${s.city}-${s.region}`}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        gnSuppressSuggestRef.current = true;
                        setGnCity(s.city);
                        setGnShowSuggestions(false);
                        setGnSuggestions([]);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between text-sm border-b border-slate-50 last:border-b-0"
                    >
                      <span className="text-slate-800">{s.city}, {s.region}</span>
                      <span className="text-xs text-slate-400">{s.areaCodes.slice(0, 2).join(", ")}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleGnSearch}
            disabled={gnLoading}
            className="w-full py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {gnLoading ? "Searching..." : "Search Available Numbers"}
          </button>

          {gnSearchDone && !gnLoading && gnMatchedCity && gnNumbers.length > 0 && !gnIsNearby && (
            <p className="text-xs text-slate-500 text-center">
              Showing numbers for <span className="font-medium text-slate-700">{gnMatchedCity}</span>
            </p>
          )}

          {gnSearchDone && !gnLoading && gnIsNearby && gnNumbers.length > 0 && (
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
              <p className="text-xs text-blue-700">
                No numbers available directly in <span className="font-semibold">{gnMatchedCity || gnCity}</span>. Showing nearby numbers from the same region. Check back in a few hours  -  local numbers often become available for short periods.
              </p>
            </div>
          )}

          {gnSearchDone && !gnLoading && gnNumbers.length === 0 && gnCity && (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center space-y-2">
              <p className="text-sm text-slate-600">
                No local numbers available near <span className="font-semibold">{gnMatchedCity || gnCity}</span>.
              </p>
              <p className="text-xs text-slate-500">
                Try a different city, or search without a city to see all available numbers.
              </p>
            </div>
          )}

          {gnNumbers.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {gnNumbers.map((n) => {
                const location = [n.city, n.region].filter(Boolean).join(", ") || n.country;
                const isPurchasing = gnPurchasing === n.phoneNumber;
                return (
                  <div
                    key={n.phoneNumber}
                    className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all bg-white"
                  >
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-primary-light)" }}>
                      <Phone className="h-4.5 w-4.5" style={{ color: "var(--color-primary)" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 tracking-wide">
                        {n.friendlyName || n.phoneNumber}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <MapPin className="h-3 w-3" />
                          {location}
                        </span>
                        {(n.capabilities?.sms || n.capabilities?.voice) && (
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-semibold">SMS</span>
                        )}
                        {n.capabilities?.voice && (
                          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-[10px] font-semibold">Voice</span>
                        )}
                        {(n.capabilities?.mms || n.capabilities?.sms || n.capabilities?.voice) && (
                          <span className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600 text-[10px] font-semibold">MMS</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleGnPurchase(n.phoneNumber)}
                      disabled={gnPurchasing !== null}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-50 flex-shrink-0 transition-colors"
                      style={{ backgroundColor: "var(--color-primary)" }}
                      onMouseEnter={(e) => !isPurchasing && (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                    >
                      {isPurchasing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5" />
                          Get
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Purchase error message */}
          {gnPurchaseError && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Unable to purchase number</p>
                <p className="text-xs text-red-600 mt-0.5">{gnPurchaseError}</p>
              </div>
              <button
                type="button"
                onClick={() => setGnPurchaseError("")}
                className="p-0.5 rounded text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </PremiumModal>

      {/* ── Connect Email Modal (Inline) ── */}
      <PremiumModal
        open={showConnectEmail}
        onClose={() => {
          setShowConnectEmail(false);
          setCeProvider("");
        }}
        title="Connect Email"
        subtitle="Link your email account to sync messages"
        icon={Mail}
        size="sm"
      >
        {!ceProvider ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 mb-4">Choose your email provider to get started.</p>

            <button
              onClick={() => handleEmailOAuth("gmail")}
              disabled={ceLoading}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                {ceLoading ? (
                  <Loader2 className="h-5 w-5 text-red-600 animate-spin" />
                ) : (
                  <Mail className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">Connect Gmail</p>
                <p className="text-xs text-slate-500">Google Workspace or personal Gmail</p>
              </div>
            </button>

            <button
              onClick={() => handleEmailOAuth("microsoft")}
              disabled={ceLoading}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                {ceLoading ? (
                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                ) : (
                  <Mail className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">Connect Outlook</p>
                <p className="text-xs text-slate-500">Microsoft 365 or Outlook.com</p>
              </div>
            </button>

            <button
              onClick={() => setCeProvider("imap_custom")}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
            >
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-slate-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">Custom IMAP</p>
                <p className="text-xs text-slate-500">Any IMAP-compatible provider</p>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-4">
              <div className="h-14 w-14 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Mail className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-1">
                Custom IMAP Integration Coming Soon
              </h3>
              <p className="text-sm text-slate-500 max-w-xs">
                Custom IMAP integration is currently in development. We're working on secure IMAP credential handling for third-party email providers.
              </p>
            </div>

            <a
              href="mailto:support@kavoa.ca?subject=Please%20prioritize%20IMAP%20email%20integration&body=Hi%2C%0A%0AI%20would%20like%20to%20use%20IMAP%20integration%20in%20Kavoa.%20Please%20prioritize%20this%20feature.%0A%0AThanks!"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Click here to pester us to prioritize this
            </a>

            <div className="flex justify-center">
              <button
                onClick={() => setCeProvider("")}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </PremiumModal>

      {/* Communication Preferences Modal */}
      <CommunicationPreferencesModal
        open={showCommPrefsModal}
        onClose={() => setShowCommPrefsModal(false)}
        onSaved={() => {
          setCommPrefsConfigured(true);
          setCommPrefsBannerDismissed(true);
          localStorage.setItem("kavoa_comm_prefs_banner_dismissed", "true");
        }}
      />
    </div>
  );
};

export default MessagesPage;
